import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Mode = "signin" | "register" | "forgot";

export default function AuthPage() {
  const { signInGoogle, signInEmail, registerEmail, forgotPassword, loading, error, clearError, firebaseUser } =
    useAuth();
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Already logged in
  if (firebaseUser) {
    navigate("/");
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalErr(null);
    clearError();
    setSubmitting(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(email);
        setResetSent(true);
      } else if (mode === "register") {
        if (password !== confirm) {
          setLocalErr("Passwords don't match");
          return;
        }
        if (password.length < 6) {
          setLocalErr("Password must be at least 6 characters");
          return;
        }
        await registerEmail(email, password);
        navigate("/");
      } else {
        await signInEmail(email, password);
        navigate("/");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    clearError();
    setLocalErr(null);
    setSubmitting(true);
    try {
      await signInGoogle();
      navigate("/");
    } finally {
      setSubmitting(false);
    }
  };

  const displayError = localErr ?? error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="/ism-new-city.png"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <p className="text-xs font-mono tracking-[0.3em] text-primary uppercase mb-3">
            ISM New World
          </p>
          <h1 className="text-4xl font-black uppercase tracking-tight text-white">
            {mode === "register"
              ? "Join The World"
              : mode === "forgot"
                ? "Reset Password"
                : "Sign In"}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            {mode === "register"
              ? "Create your ISM New World account"
              : mode === "forgot"
                ? "We'll send a reset link to your email"
                : "Welcome back to the universe"}
          </p>
        </div>

        <div className="bg-card/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Error */}
          {displayError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-6">
              {displayError}
            </div>
          )}

          {/* Reset sent */}
          {resetSent && (
            <div className="bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg px-4 py-3 mb-6">
              Password reset email sent — check your inbox.
            </div>
          )}

          {/* Google */}
          {mode !== "forgot" && (
            <>
              <Button
                onClick={handleGoogle}
                disabled={submitting || loading}
                variant="outline"
                className="w-full flex items-center gap-3 h-12 text-sm font-medium border-white/20 hover:bg-white/5"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 border-t border-white/10" />
                <span className="text-muted-foreground text-xs uppercase tracking-wider">
                  or
                </span>
                <div className="flex-1 border-t border-white/10" />
              </div>
            </>
          )}

          {/* Email form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11"
              />
            </div>

            {mode !== "forgot" && (
              <div>
                <Label htmlFor="password" className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11"
                />
              </div>
            )}

            {mode === "register" && (
              <div>
                <Label htmlFor="confirm" className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Confirm Password
                </Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || loading}
              className="w-full h-11 bg-primary text-primary-foreground font-semibold uppercase tracking-wide text-sm"
            >
              {submitting
                ? "Please wait…"
                : mode === "forgot"
                  ? "Send Reset Link"
                  : mode === "register"
                    ? "Create Account"
                    : "Sign In"}
            </Button>
          </form>

          {/* Mode toggles */}
          <div className="mt-6 text-center text-sm text-muted-foreground space-y-2">
            {mode === "signin" && (
              <>
                <p>
                  No account?{" "}
                  <button
                    onClick={() => { setMode("register"); clearError(); }}
                    className="text-primary hover:underline font-medium"
                  >
                    Create one
                  </button>
                </p>
                <p>
                  <button
                    onClick={() => { setMode("forgot"); clearError(); }}
                    className="text-muted-foreground hover:text-white hover:underline"
                  >
                    Forgot password?
                  </button>
                </p>
              </>
            )}
            {mode === "register" && (
              <p>
                Already have an account?{" "}
                <button
                  onClick={() => { setMode("signin"); clearError(); }}
                  className="text-primary hover:underline font-medium"
                >
                  Sign in
                </button>
              </p>
            )}
            {mode === "forgot" && (
              <p>
                <button
                  onClick={() => { setMode("signin"); clearError(); setResetSent(false); }}
                  className="text-primary hover:underline font-medium"
                >
                  ← Back to sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
